import React from 'react';
import { ArrowLeft, Home, Tag, Package, LogOut, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StoreCustomization } from '@/hooks/useSupplierStore';
import { useAuth } from '@/context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { getSupplierTheme } from '@/utils/supplierCustomizationCache';

interface StoreNavTabsProps {
  activeTab?: 'inicio' | 'promocoes' | 'pedidos';
  showBackButton?: boolean;
  supplierName?: string;
  supplierSlug?: string;
  customization?: StoreCustomization | null;
  eventCode?: string | null;
  /** Quando true, não desenha o badge do código do evento aqui (ex.: quando ele já aparece no canto do banner). */
  hideEventCodeBadge?: boolean;
  onShare?: () => Promise<string | null>;
  onBack?: () => void | Promise<void>;
  backLabel?: string;
}

export const StoreNavTabs: React.FC<StoreNavTabsProps> = ({
  activeTab = 'inicio',
  showBackButton = true,
  supplierName,
  supplierSlug,
  customization,
  eventCode,
  hideEventCodeBadge = false,
  onShare,
  onBack,
  backLabel = 'Todos Cardápios',
}) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // Allow supplier to hide the top navigation header
  if (customization && (customization as any).showHeader === false) {
    return null;
  }

  const headerColor = customization?.headerColor || 'hsl(var(--primary))';
  const buttonTextColor = customization?.buttonTextColor || 'hsl(var(--primary-foreground))';

  // Marca do fornecedor: props têm prioridade; senão, cache de customização.
  let cachedTheme: ReturnType<typeof getSupplierTheme> | null = null;
  try {
    cachedTheme = getSupplierTheme();
  } catch {
    cachedTheme = null;
  }
  const brandName = supplierName || cachedTheme?.supplierName || '';
  const brandLogoUrl =
    (customization as any)?.showLogo === false ? null : cachedTheme?.logoUrl || null;

  const tabs = [
    { id: 'inicio', label: 'Início', icon: Home },
    { id: 'promocoes', label: 'Promoções', icon: Tag },
    { id: 'pedidos', label: 'Pedidos', icon: Package },
  ];

  const handleTabClick = (tabId: string) => {
    switch (tabId) {
      case 'inicio':
        break;
      case 'promocoes':
        break;
      case 'pedidos':
        if (supplierSlug) {
          navigate(`/portal-cliente/${supplierSlug}`);
        }
        break;
    }
  };

  const handleLogout = async () => {
    await signOut();
    // Stay on the current supplier store page (just reload without auth)
    window.location.reload();
  };

  return (
    <div 
      className="sticky top-0 z-40 w-full shadow-sm"
      style={{ backgroundColor: headerColor }}
    >
      <div className="container-custom">
        <div className="relative flex items-center justify-between h-12 sm:h-14">
          {/* Back button (left) */}
          {onBack ? (
            <button
              onClick={() => { void onBack(); }}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors"
              style={{ color: buttonTextColor }}
              aria-label={backLabel}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{backLabel}</span>
            </button>
          ) : (
            <div className="w-10" />
          )}

          {/* Logo + supplier name (center) */}
          <div className={`pointer-events-none absolute inset-y-0 ${eventCode && !hideEventCodeBadge ? 'left-[6.75rem] right-[6.75rem]' : 'left-14 right-14'} sm:left-28 sm:right-28 flex min-w-0 items-center justify-center gap-2`}>
            {brandLogoUrl && (
              <img
                src={brandLogoUrl}
                alt={brandName || 'Fornecedor'}
                className="h-7 w-auto shrink-0 object-contain rounded"
              />
            )}
            {brandName && (
              <span
                className="font-semibold text-xs sm:text-sm md:text-base leading-tight text-center break-words min-w-0"
                style={{ color: buttonTextColor }}
              >
                {brandName}
              </span>
            )}
          </div>

          {/* Event Code Badge */}
          {eventCode && !hideEventCodeBadge && (
            <span 
              className="ml-auto text-xs font-mono px-2.5 py-1 rounded-full tracking-wide mr-2"
              style={{ color: buttonTextColor, backgroundColor: 'rgba(255,255,255,0.15)' }}
            >
              {eventCode}
            </span>
          )}

          {/* User menu */}
          <div className="flex items-center gap-1">
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    style={{ color: buttonTextColor }}
                  >
                    <User className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                  <DropdownMenuItem disabled className="text-xs text-muted-foreground truncate">
                    {user.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
